import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ParamType } from "../../../types/grid.ts";
import type { InputParam } from "../../../types/grid.ts";
import type { ServerSetting } from "../../../types/session.ts";
import type { AdvancedFilterProps } from "./types.ts";
import {
  getBrowserTimeZone, getClientTimeZone,
  getTimeZoneOffsetMinutes, getWallClockInZone,
} from "../../../util/clientEnv.ts";
import styles from "./dateFilter.module.scss";

interface SimpleSettings {
  isEndOfReport: boolean;
  includeIfNull: boolean;
  includeOffset: boolean;
  defaultMode: "empty" | "setting" | "subtractMonth" | "now" | "endLastMonth" | "startLastMonth";
}

function parseSettings(raw: string | undefined): SimpleSettings {
  const result: SimpleSettings = {
    isEndOfReport: false,
    includeIfNull: false,
    includeOffset: false,
    defaultMode: "empty",
  };
  if (!raw) return result;
  for (const part of raw.split("|")) {
    switch (part.trim().toUpperCase()) {
      case "DEFAULTTOEMPTY":
        result.defaultMode = "empty";
        break;
      case "DEFAULTTOSETTING":
        result.defaultMode = "setting";
        break;
      case "DEFAULTTOMONTHLY":
        result.defaultMode = "subtractMonth";
        break;
      case "DEFAULTTONOW":
        result.defaultMode = "now";
        break;
      case "ENDLASTMONTH":
        result.defaultMode = "endLastMonth";
        result.isEndOfReport = true;
        break;
      case "STARTLASTMONTH":
        result.defaultMode = "startLastMonth";
        break;
      case "ISENDOFREPORT":
        result.isEndOfReport = true;
        break;
      case "INCLUDEIFNULL":
        result.includeIfNull = true;
        break;
      case "INCLUDEOFFSET":
      case "TIMEZONE":
        // Append the client zone's UTC offset (" +HH:mm") to the emitted value so
        // it round-trips a DATETIMEOFFSET parameter/column (e.g. the statement FNs).
        result.includeOffset = true;
        break;
    }
  }
  return result;
}

function getServerSettingInt(
  serverSettings: ServerSetting[],
  key: string,
  fallback: number,
): number {
  for (const s of serverSettings) {
    if (s.Key === key) {
      const v = Number(s.Value);
      return isNaN(v) ? fallback : v;
    }
  }
  return fallback;
}

/** `fraction` is appended as ".fffffff" (7-digit, datetimeoffset(7) max) — used to
 *  emit the maximal end-of-day instant ("23:59:59.9999999") so the bound includes
 *  the whole final second regardless of the compared column's precision. JS Date
 *  only resolves to ms, so the sub-ms digits are a literal, not derived. */
function formatDate(d: Date, includeOffset: boolean, fraction = ""): string {
  const zone = getClientTimeZone();
  const overridden = zone !== getBrowserTimeZone();
  let yyyy: number, MM: number, dd: number, HH: number, mm: number, off: number;
  if (overridden) {
    const w = getWallClockInZone(zone, d);
    yyyy = w.year; MM = w.month; dd = w.day; HH = w.hour; mm = w.minute;
    off = getTimeZoneOffsetMinutes(zone, d);
  } else {
    yyyy = d.getFullYear(); MM = d.getMonth() + 1; dd = d.getDate();
    HH = d.getHours(); mm = d.getMinutes();
    off = -d.getTimezoneOffset();
  }
  // Seconds are timezone-invariant (offsets are whole minutes), so read them
  // straight off the Date regardless of the override-zone path above.
  const ss = d.getSeconds();
  let s = `${yyyy}-${String(MM).padStart(2, "0")}-${String(dd).padStart(2, "0")} ${String(HH).padStart(2, "0")}:${String(mm).padStart(2, "0")}:${String(ss).padStart(2, "0")}`;
  if (fraction) s += `.${fraction}`;
  if (includeOffset) {
    const sign = off >= 0 ? "+" : "-";
    const absOff = Math.abs(off);
    s += ` ${sign}${String(Math.floor(absOff / 60)).padStart(2, "0")}:${String(absOff % 60).padStart(2, "0")}`;
  }
  return s;
}

/** Convert backend "YYYY-MM-DD HH:mm" to "YYYY-MM-DD" for date input. */
function toInputValue(backendStr: string): string {
  if (!backendStr) return "";
  return backendStr.substring(0, 10);
}

/** Build backend "YYYY-MM-DD HH:mm[ +OFF]" from a date-only input value.
 *  Override path treats the user-typed date as the wall-clock day in the
 *  override zone (skipping the browser-local Date constructor that would
 *  re-interpret it as browser time and silently shift days near DST). */
function buildBackendFromDateInput(inputVal: string, isEndOfReport: boolean, includeOffset: boolean): string {
  if (!inputVal || !/^\d{4}-\d{2}-\d{2}$/.test(inputVal)) return "";
  const zone = getClientTimeZone();
  if (zone === getBrowserTimeZone()) {
    const d = new Date(inputVal + "T00:00:00");
    if (isNaN(d.getTime())) return "";
    if (isEndOfReport) d.setHours(23, 59, 59, 0);
    return formatDate(d, includeOffset, isEndOfReport ? "9999999" : "");
  }
  const HH = isEndOfReport ? "23" : "00";
  const mm = isEndOfReport ? "59" : "00";
  const ss = isEndOfReport ? "59" : "00";
  const frac = isEndOfReport ? ".9999999" : "";
  let s = `${inputVal} ${HH}:${mm}:${ss}${frac}`;
  if (includeOffset) {
    const [yy, MM, dd] = inputVal.split("-").map(Number);
    const approx = new Date(Date.UTC(yy!, (MM ?? 1) - 1, dd ?? 1, Number(HH), Number(mm)));
    const off1 = getTimeZoneOffsetMinutes(zone, approx);
    const refined = new Date(approx.getTime() - off1 * 60000);
    const off = getTimeZoneOffsetMinutes(zone, refined);
    const sign = off >= 0 ? "+" : "-";
    const abs = Math.abs(off);
    s += ` ${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
  }
  return s;
}

function computeDefault(
  settings: SimpleSettings,
  serverSettings: ServerSetting[],
): string {
  const now = new Date();
  let d: Date;
  switch (settings.defaultMode) {
    case "now":
      d = new Date(now);
      break;
    case "subtractMonth":
      d = new Date(now);
      d.setMonth(d.getMonth() - 1);
      break;
    case "endLastMonth":
      d = new Date(now.getFullYear(), now.getMonth(), 0);
      break;
    case "startLastMonth":
      d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      break;
    case "setting": {
      const days = getServerSettingInt(serverSettings, "SLAdmin.DefaultSearchStartDate", 7);
      d = new Date(now);
      d.setDate(d.getDate() - days);
      break;
    }
    default:
      return "";
  }
  d.setHours(settings.isEndOfReport ? 23 : 0, settings.isEndOfReport ? 59 : 0, settings.isEndOfReport ? 59 : 0, 0);
  return formatDate(d, settings.includeOffset, settings.isEndOfReport ? "9999999" : "");
}

function buildParam(
  colName: string,
  dateStr: string,
  paramType: ParamType,
  includeIfNull: boolean,
): InputParam[] {
  if (dateStr) {
    return [{ sColumn: colName, oValue: dateStr, Type: paramType }];
  }
  if (includeIfNull) {
    return [{ sColumn: colName, oValue: null, Type: paramType }];
  }
  return [];
}

export function DateFilterSimple({ info, isParameter, value, onChange, onSearch, cache }: AdvancedFilterProps) {
  const colName = info.ColumnName;
  const settings = useMemo(() => parseSettings(info.FilterSettings), [info.FilterSettings]);

  const outParamType = useMemo(() => {
    if (isParameter) return ParamType.Specified;
    return settings.isEndOfReport ? ParamType.Equal : ParamType.EqualOrLargerThen;
  }, [isParameter, settings.isEndOfReport]);

  const defaultVal = useMemo(
    () => computeDefault(settings, cache?.ServerSettings ?? []),
    [settings, cache?.ServerSettings],
  );

  // If an entry exists, its oValue is authoritative (even if ""),
  // preserving explicitly-cleared state on reload. Otherwise fall back to default.
  const [dateVal, setDateVal] = useState(() => {
    const p = value.find((v) => v.sColumn === colName);
    // oValue === true is the codec's checkbox-default sentinel — discard it
    // for the date input (see filterUrlCodec.ts + dateFilter.tsx).
    if (p !== undefined && p.oValue !== true) return String(p.oValue ?? "");
    return defaultVal;
  });

  const mountedWithValues = useRef(value.length > 0);
  const didEmitDefaults = useRef(false);
  useEffect(() => {
    if (didEmitDefaults.current) return;
    didEmitDefaults.current = true;
    if (mountedWithValues.current) return;
    if (defaultVal || settings.includeIfNull) {
      onChange(buildParam(colName, defaultVal, outParamType, settings.includeIfNull));
    }
  }, [defaultVal, colName, outParamType, settings.includeIfNull, onChange]);

  // Reset when cleared externally
  useEffect(() => {
    if (value.length === 0 && didEmitDefaults.current) {
      setDateVal(defaultVal);
      if (defaultVal || settings.includeIfNull) {
        onChange(buildParam(colName, defaultVal, outParamType, settings.includeIfNull));
      }
    }
  }, [value, defaultVal, colName, outParamType, settings.includeIfNull, onChange]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const backend = buildBackendFromDateInput(e.target.value, settings.isEndOfReport, settings.includeOffset);
      setDateVal(backend);
      onChange(buildParam(colName, backend, outParamType, settings.includeIfNull));
    },
    [colName, outParamType, settings, onChange],
  );

  const handleToday = useCallback(() => {
    const d = new Date();
    d.setHours(settings.isEndOfReport ? 23 : 0, settings.isEndOfReport ? 59 : 0, settings.isEndOfReport ? 59 : 0, 0);
    const backend = formatDate(d, settings.includeOffset, settings.isEndOfReport ? "9999999" : "");
    setDateVal(backend);
    onChange(buildParam(colName, backend, outParamType, settings.includeIfNull));
  }, [colName, outParamType, settings, onChange]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") onSearch();
    },
    [onSearch],
  );

  return (
    <div className={styles.dateRange}>
      <input
        type="date"
        value={toInputValue(dateVal)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        className={styles.dateInput}
      />
      <button type="button" className={styles.todayBtn} onClick={handleToday}>Today</button>
    </div>
  );
}
